import React, { useState, useEffect, useRef } from "react";
import { useApi } from "../../hooks/useApi";
import { useTheme } from "../../contexts/ThemeContext";
import { AdminDesignSystem, ADMIN_ROOT } from "./adminDesignSystem";
import AdminSidebar from "./AdminSidebar";
import toast from "react-hot-toast";
import { Select, ConfigProvider, theme as antdTheme } from "antd";
import {
  Link as LinkIcon,
  Search,
  Building,
  Copy,
  ExternalLink,
  QrCode,
  XCircle,
  CheckCircle,
  IndianRupee,
  Users,
  Sparkles,
  RefreshCw,
  FileText,
} from "lucide-react";

const API_BASE = "/api/v1/admin/razorpay-links";

const RazorpayLinkGenerator = () => {
  const api = useApi();
  const { mode } = useTheme();
  const isDark = mode === "dark";

  // form state
  const [orgQuery, setOrgQuery] = useState("");
  const [orgResults, setOrgResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgDetails, setOrgDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [plan, setPlan] = useState("standard");
  const [customRate, setCustomRate] = useState("");
  const [customTier, setCustomTier] = useState("standard"); // when plan=custom, which tier does this rate belong to?
  const [seats, setSeats] = useState(1);
  const [description, setDescription] = useState("");
  const [expireInDays, setExpireInDays] = useState("");

  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);

  // list state
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);

  const searchTimer = useRef(null);

  useEffect(() => {
    fetchLinks();
  }, []);

  // debounce org search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const q = orgQuery.trim();
    if (q.length < 2) {
      setOrgResults([]);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await api.get(`${API_BASE}/orgs/search?q=${encodeURIComponent(q)}`);
        setOrgResults(res.data?.data || []);
      } catch (err) {
        console.error("[RazorpayLinkGenerator] search error", err);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [orgQuery, api]);

  const fetchLinks = async () => {
    try {
      setLoadingLinks(true);
      const res = await api.get(`${API_BASE}/links?limit=50`);
      setLinks(res.data?.data || []);
    } catch (err) {
      console.error("[RazorpayLinkGenerator] list error", err);
    } finally {
      setLoadingLinks(false);
    }
  };

  const selectOrg = async (org) => {
    setSelectedOrg(org);
    setOrgQuery("");
    setOrgResults([]);
    setOrgDetails(null);
    setLastCreated(null);
    setQrUrl(null);
    try {
      setLoadingDetails(true);
      const res = await api.get(`${API_BASE}/orgs/${org._id}/details`);
      setOrgDetails(res.data?.data);
      // Sensible defaults from current org state
      const cur = res.data?.data?.organization;
      if (cur?.orgPlanSelection === "premium") setPlan("premium");
      else setPlan("standard");
      setSeats(Math.max(1, cur?.paidMemberSlots || 1));
    } catch (err) {
      toast.error("Failed to load org details");
      console.error("[RazorpayLinkGenerator] details error", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const clearSelection = () => {
    setSelectedOrg(null);
    setOrgDetails(null);
    setLastCreated(null);
    setQrUrl(null);
  };

  const previewAmount = () => {
    const s = Math.max(1, parseInt(seats) || 1);
    let per;
    if (plan === "custom") per = Number(customRate) || 0;
    else if (plan === "premium") per = 649;
    else per = 299;
    return { perSeat: per, seats: s, total: per * s };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedOrg?._id) {
      toast.error("Select an organization first");
      return;
    }
    if (plan === "custom" && (!customRate || Number(customRate) < 1)) {
      toast.error("Enter a valid custom rate");
      return;
    }
    if (!seats || Number(seats) < 1) {
      toast.error("Seats must be at least 1");
      return;
    }

    try {
      setCreating(true);
      setLastCreated(null);
      setQrUrl(null);

      const body = {
        organizationId: selectedOrg._id,
        plan,
        seats: Number(seats),
      };
      if (plan === "custom") {
        body.customRate = Number(customRate);
        body.customTier = customTier; // 'standard' | 'premium'
      }
      if (description.trim()) body.description = description.trim();
      if (expireInDays && Number(expireInDays) > 0) body.expireInDays = Number(expireInDays);

      const res = await api.post(`${API_BASE}/create`, body);
      const data = res.data?.data;
      setLastCreated(data);
      toast.success("Payment link created");

      // Load QR
      if (data?.link?.id) {
        try {
          const qrRes = await api.get(`${API_BASE}/links/${data.link.id}/qr`);
          setQrUrl(qrRes.data?.data?.qrUrl || null);
        } catch {
          /* qr is best-effort */
        }
      }

      // Refresh org details (razorpayCustomerId may have been just set)
      selectOrg(selectedOrg);
      fetchLinks();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to create link";
      toast.error(msg);
      console.error("[RazorpayLinkGenerator] create error", err);
    } finally {
      setCreating(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied");
  };

  const cancelLink = async (linkId) => {
    if (!window.confirm("Cancel this payment link? Client will no longer be able to pay via this URL.")) return;
    try {
      await api.post(`${API_BASE}/links/${linkId}/cancel`);
      toast.success("Link cancelled");
      fetchLinks();
      if (lastCreated?.link?.id === linkId) setLastCreated(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Cancel failed");
    }
  };

  const preview = previewAmount();

  const cardBg = isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200";
  const inputCls = isDark
    ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500"
    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400";
  const labelCls = isDark ? "text-gray-300" : "text-gray-700";

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: { colorPrimary: "#6366f1", borderRadius: 8 },
      }}
    >
    <div className={`${ADMIN_ROOT} h-screen flex overflow-hidden ${isDark ? "bg-black" : "bg-gray-100"}`}>
      <AdminDesignSystem />
      <AdminSidebar activeKey="razorpay-links" />

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6 px-5 sm:px-6 w-full space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${isDark ? "bg-indigo-500/20 border border-indigo-500/30" : "bg-indigo-50 border border-indigo-200"}`}>
                  <LinkIcon className={`h-5 w-5 ${isDark ? "text-indigo-400" : "text-indigo-600"}`} />
                </div>
                <div>
                  <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                    Razorpay Payment Links
                  </h1>
                  <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Generate one-time payment links for any organization on Razorpay.
                  </p>
                </div>
              </div>
              <button
                onClick={fetchLinks}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isDark ? "bg-gray-800 text-gray-300 hover:bg-gray-700" : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"}`}
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>

            {/* Generator card */}
            <div className={`rounded-2xl border ${cardBg} p-6 space-y-5`}>
              <div className="flex items-center gap-2">
                <Sparkles className={`h-5 w-5 ${isDark ? "text-indigo-400" : "text-indigo-600"}`} />
                <h2 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Generate a new link
                </h2>
              </div>

              {/* Org search */}
              <div>
                <label className={`block text-xs font-medium mb-1 ${labelCls}`}>Organization</label>
                {!selectedOrg ? (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={orgQuery}
                        onChange={(e) => setOrgQuery(e.target.value)}
                        placeholder="Search by org name or admin email..."
                        className={`w-full pl-9 pr-3 py-2.5 rounded-lg border ${inputCls} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      />
                    </div>
                    {orgResults.length > 0 && (
                      <div className={`absolute z-10 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border shadow-lg ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
                        {orgResults.map((o) => (
                          <button
                            key={o._id}
                            type="button"
                            onClick={() => selectOrg(o)}
                            className={`w-full text-left px-3 py-2 text-sm ${isDark ? "hover:bg-gray-800 text-gray-200" : "hover:bg-gray-50 text-gray-800"}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 min-w-0">
                                <Building className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <div className="min-w-0">
                                  <div className="font-medium truncate">{o.name}</div>
                                  {o.adminEmail && (
                                    <div className={`text-xs truncate ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                                      {o.adminEmail}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span
                                  className={`text-[10px] px-2 py-0.5 rounded-full ${
                                    o.razorpayReady
                                      ? isDark ? "bg-green-500/20 text-green-400" : "bg-green-100 text-green-700"
                                      : isDark ? "bg-yellow-500/20 text-yellow-400" : "bg-yellow-100 text-yellow-700"
                                  }`}
                                >
                                  {o.razorpayReady ? "Razorpay Ready" : "Needs Prep"}
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searching && (
                      <div className={`text-xs mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>Searching…</div>
                    )}
                  </div>
                ) : (
                  <div className={`flex items-center justify-between p-3 rounded-lg border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Building className={`h-4 w-4 ${isDark ? "text-indigo-400" : "text-indigo-600"} flex-shrink-0`} />
                      <div className="min-w-0">
                        <div className={`font-medium truncate ${isDark ? "text-white" : "text-gray-900"}`}>
                          {selectedOrg.name}
                        </div>
                        {selectedOrg.adminEmail && (
                          <div className={`text-xs truncate ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                            {selectedOrg.adminEmail}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearSelection}
                      className={`text-xs px-2 py-1 rounded ${isDark ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-200"}`}
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>

              {/* Current state summary */}
              {loadingDetails && (
                <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>Loading org state…</div>
              )}
              {orgDetails && (
                <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 rounded-lg border ${isDark ? "bg-gray-800/50 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
                  <Stat isDark={isDark} label="Plan" value={orgDetails.organization.orgPlanSelection || "-"} />
                  <Stat isDark={isDark} label="Slots" value={`${orgDetails.organization.usedMemberSlots || 0}/${orgDetails.organization.paidMemberSlots || 0}`} />
                  <Stat isDark={isDark} label="Gateway" value={orgDetails.organization.paymentGateway || "-"} />
                  <Stat isDark={isDark} label="Currency" value={orgDetails.organization.orgPlanCurrency || "-"} />
                  <Stat isDark={isDark} label="Status" value={orgDetails.organization.subscriptionStatus || "-"} />
                  <Stat isDark={isDark} label="Razorpay Ready" value={orgDetails.razorpayReady ? "Yes" : "No"} success={orgDetails.razorpayReady} />
                  <Stat isDark={isDark} label="Admin" value={orgDetails.admin?.email || "-"} small />
                  <Stat isDark={isDark} label="Customer ID" value={orgDetails.organization.razorpayCustomerId || "will create"} small />
                </div>
              )}

              {/* Form */}
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${labelCls}`}>Plan</label>
                    <Select
                      value={plan}
                      onChange={(v) => setPlan(v)}
                      size="large"
                      className="w-full"
                      style={{ width: "100%" }}
                      options={[
                        { value: "standard", label: "Standard (₹299/seat)" },
                        { value: "premium",  label: "Premium (₹649/seat)" },
                        { value: "custom",   label: "Custom rate" },
                      ]}
                    />
                  </div>

                  {plan === "custom" ? (
                    <>
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${labelCls}`}>Base tier for custom rate</label>
                        <Select
                          value={customTier}
                          onChange={(v) => setCustomTier(v)}
                          size="large"
                          className="w-full"
                          style={{ width: "100%" }}
                          options={[
                            { value: "standard", label: "Standard tier (limited features)" },
                            { value: "premium",  label: "Premium tier (all features)" },
                          ]}
                        />
                        <div className={`text-[10px] mt-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                          Which plan tier does this custom rate belong to? Controls feature access.
                        </div>
                      </div>
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${labelCls}`}>Custom rate ₹/seat</label>
                        <div className="relative">
                          <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="number"
                            min="1"
                            value={customRate}
                            onChange={(e) => setCustomRate(e.target.value)}
                            placeholder="240"
                            className={`w-full pl-9 pr-3 py-2.5 rounded-lg border ${inputCls} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${labelCls}`}>Per-seat rate</label>
                      <div className={`px-3 py-2.5 rounded-lg border ${isDark ? "bg-gray-800 border-gray-700 text-gray-400" : "bg-gray-50 border-gray-200 text-gray-600"}`}>
                        ₹{plan === "premium" ? 649 : 299}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className={`block text-xs font-medium mb-1 ${labelCls}`}>Seats</label>
                    <div className="relative">
                      <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="number"
                        min="1"
                        value={seats}
                        onChange={(e) => setSeats(e.target.value)}
                        className={`w-full pl-9 pr-3 py-2.5 rounded-lg border ${inputCls} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${labelCls}`}>Description (optional)</label>
                    <input
                      type="text"
                      maxLength={200}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="TeamGrid standard - 4 seats (1 month)"
                      className={`w-full px-3 py-2.5 rounded-lg border ${inputCls} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${labelCls}`}>Expire in days (optional)</label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={expireInDays}
                      onChange={(e) => setExpireInDays(e.target.value)}
                      placeholder="No expiry"
                      className={`w-full px-3 py-2.5 rounded-lg border ${inputCls} focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    />
                  </div>
                </div>

                {/* Preview */}
                <div className={`flex items-center justify-between p-3 rounded-lg ${isDark ? "bg-indigo-500/10 border border-indigo-500/30" : "bg-indigo-50 border border-indigo-200"}`}>
                  <div className={`text-xs ${isDark ? "text-indigo-300" : "text-indigo-700"}`}>
                    {preview.seats} seat(s) × ₹{preview.perSeat} = link amount
                  </div>
                  <div className={`text-lg font-bold ${isDark ? "text-white" : "text-indigo-900"}`}>
                    ₹{preview.total.toLocaleString("en-IN")}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!selectedOrg || creating}
                  className={`w-full py-3 rounded-lg font-medium transition ${
                    !selectedOrg || creating
                      ? isDark ? "bg-gray-800 text-gray-500" : "bg-gray-200 text-gray-400"
                      : "bg-indigo-600 hover:bg-indigo-500 text-white"
                  }`}
                >
                  {creating ? "Creating link…" : "Generate Payment Link"}
                </button>
              </form>
            </div>

            {/* Last created link result */}
            {lastCreated && (
              <div className={`rounded-2xl border ${isDark ? "bg-green-500/5 border-green-500/30" : "bg-green-50 border-green-200"} p-6 space-y-4`}>
                <div className="flex items-center gap-2">
                  <CheckCircle className={`h-5 w-5 ${isDark ? "text-green-400" : "text-green-600"}`} />
                  <h2 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                    Link created
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <Field
                      isDark={isDark}
                      label="Payment URL"
                      value={lastCreated.link.short_url}
                      copy={copyText}
                      openable
                    />
                    <Field
                      isDark={isDark}
                      label="Link ID"
                      value={lastCreated.link.id}
                      copy={copyText}
                    />
                    <div className={`grid grid-cols-2 gap-3`}>
                      <Field isDark={isDark} label="Amount" value={`₹${lastCreated.link.amount.toLocaleString("en-IN")}`} />
                      <Field isDark={isDark} label="Status" value={lastCreated.link.status} />
                    </div>
                    {lastCreated.prep && (
                      <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"} space-y-0.5`}>
                        {lastCreated.prep.setGateway && <div>• Gateway set to razorpay</div>}
                        {lastCreated.prep.setCurrency && <div>• Currency set to INR</div>}
                        {lastCreated.prep.createdCustomer && <div>• Razorpay customer created: {lastCreated.prep.customerId}</div>}
                        {!lastCreated.prep.setGateway && !lastCreated.prep.setCurrency && !lastCreated.prep.createdCustomer && (
                          <div>• Org was already Razorpay-ready</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-center justify-center">
                    {qrUrl ? (
                      <img src={qrUrl} alt="QR code" className="w-48 h-48 rounded-lg border border-gray-200 bg-white p-2" />
                    ) : (
                      <div className={`w-48 h-48 rounded-lg flex items-center justify-center ${isDark ? "bg-gray-800" : "bg-gray-100"}`}>
                        <QrCode className="h-10 w-10 text-gray-400" />
                      </div>
                    )}
                    <div className={`text-xs mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>Scan QR to pay</div>
                  </div>
                </div>
              </div>
            )}

            {/* Recent links table */}
            <div className={`rounded-2xl border ${cardBg} p-6`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FileText className={`h-5 w-5 ${isDark ? "text-indigo-400" : "text-indigo-600"}`} />
                  <h2 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                    Recent panel-generated links
                  </h2>
                </div>
                <button
                  onClick={fetchLinks}
                  className={`text-xs px-2 py-1 rounded ${isDark ? "text-gray-300 hover:bg-gray-800" : "text-gray-600 hover:bg-gray-100"}`}
                >
                  {loadingLinks ? "Loading…" : "Refresh"}
                </button>
              </div>

              {links.length === 0 ? (
                <div className={`text-center py-8 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  {loadingLinks ? "Loading…" : "No links generated yet."}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className={`text-xs uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      <tr className={`${isDark ? "border-b border-gray-800" : "border-b border-gray-200"}`}>
                        <th className="text-left py-2 px-2">Description</th>
                        <th className="text-left py-2 px-2">Org</th>
                        <th className="text-right py-2 px-2">Amount</th>
                        <th className="text-center py-2 px-2">Status</th>
                        <th className="text-center py-2 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {links.map((l) => (
                        <tr key={l.id} className={`${isDark ? "border-b border-gray-800" : "border-b border-gray-100"}`}>
                          <td className={`py-2 px-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                            <div className="max-w-xs truncate" title={l.description}>{l.description || "-"}</div>
                            <div className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>{l.id}</div>
                          </td>
                          <td className={`py-2 px-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            <div className="text-[11px]">Org: {l.notes?.organizationId?.slice(-6) || "-"}</div>
                            <div className="text-[11px]">Seats: {l.notes?.totalSeats || "-"}</div>
                          </td>
                          <td className={`py-2 px-2 text-right font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                            ₹{l.amount.toLocaleString("en-IN")}
                          </td>
                          <td className="py-2 px-2 text-center">
                            <StatusPill isDark={isDark} status={l.status} />
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                title="Copy URL"
                                onClick={() => copyText(l.short_url)}
                                className={`p-1.5 rounded ${isDark ? "hover:bg-gray-800 text-gray-400" : "hover:bg-gray-100 text-gray-600"}`}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                              <a
                                title="Open"
                                href={l.short_url}
                                target="_blank"
                                rel="noreferrer"
                                className={`p-1.5 rounded ${isDark ? "hover:bg-gray-800 text-gray-400" : "hover:bg-gray-100 text-gray-600"}`}
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                              {l.status !== "paid" && l.status !== "cancelled" && (
                                <button
                                  title="Cancel"
                                  onClick={() => cancelLink(l.id)}
                                  className={`p-1.5 rounded ${isDark ? "hover:bg-red-500/20 text-red-400" : "hover:bg-red-50 text-red-600"}`}
                                >
                                  <XCircle className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
    </ConfigProvider>
  );
};

// ---------- small display helpers ----------

const Stat = ({ isDark, label, value, success, small }) => (
  <div>
    <div className={`text-[10px] uppercase ${isDark ? "text-gray-500" : "text-gray-500"}`}>{label}</div>
    <div className={`${small ? "text-xs" : "text-sm"} font-medium truncate ${success ? (isDark ? "text-green-400" : "text-green-600") : isDark ? "text-white" : "text-gray-900"}`} title={String(value)}>
      {String(value ?? "-")}
    </div>
  </div>
);

const Field = ({ isDark, label, value, copy, openable }) => (
  <div>
    <div className={`text-[10px] uppercase mb-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>{label}</div>
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}>
      <div className={`text-sm truncate flex-1 ${isDark ? "text-gray-200" : "text-gray-800"}`}>{value}</div>
      {copy && (
        <button
          onClick={() => copy(value)}
          className={`p-1 rounded ${isDark ? "hover:bg-gray-800 text-gray-400" : "hover:bg-gray-100 text-gray-600"}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      )}
      {openable && (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className={`p-1 rounded ${isDark ? "hover:bg-gray-800 text-gray-400" : "hover:bg-gray-100 text-gray-600"}`}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  </div>
);

const StatusPill = ({ isDark, status }) => {
  const map = {
    paid: isDark ? "bg-green-500/20 text-green-400" : "bg-green-100 text-green-700",
    created: isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700",
    partially_paid: isDark ? "bg-yellow-500/20 text-yellow-400" : "bg-yellow-100 text-yellow-700",
    cancelled: isDark ? "bg-gray-500/20 text-gray-400" : "bg-gray-100 text-gray-700",
    expired: isDark ? "bg-red-500/20 text-red-400" : "bg-red-100 text-red-700",
  };
  const cls = map[status] || (isDark ? "bg-gray-500/20 text-gray-400" : "bg-gray-100 text-gray-700");
  return <span className={`text-[10px] px-2 py-0.5 rounded-full ${cls}`}>{status}</span>;
};

export default RazorpayLinkGenerator;
